import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { giftService } from '../../services/gift.service';
import { getApiUrl } from '../../config/api';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { classActions, studentActions, notificationActions } from '../../redux/actions';
import GiftToClass from './GiftToClass';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}`;
const spin = keyframes`0%{transform:scale(1)}50%{transform:scale(1.08)}100%{transform:scale(1)}`;

// ── Color tokens (blue theme) ────────────────────────────────────────────────
const C = {
  primary:'#2563eb',
  primaryDark:'#1d4ed8',
  primaryLight:'rgba(37,99,235,.1)',
  primaryBorder:'rgba(37,99,235,.3)',
  bg:'#f8fafc',
  surface:'#ffffff',
  surfaceBorder:'#e2e8f0',
  text:'#1e293b',
  textMuted:'#64748b',
  textLight:'#94a3b8',
  danger:'#ef4444',
  success:'#10b981',
};

// ── Tab bar ──────────────────────────────────────────────────────────────────
const TabBar = styled.div`display:flex;gap:8px;max-width:1100px;margin:0 auto 24px;flex-wrap:wrap;`;
const Tab = styled.button<{$active:boolean}>`
  padding:9px 20px;border-radius:10px;
  border:2px solid ${p=>p.$active?C.primary:C.surfaceBorder};
  background:${p=>p.$active?C.primary:'transparent'};
  color:${p=>p.$active?'white':C.textMuted};
  font-weight:700;font-size:14px;cursor:pointer;transition:all .18s;
  &:hover{border-color:${C.primary};color:${p=>p.$active?'white':C.primary};background:${p=>p.$active?C.primaryDark:C.primaryLight};}
`;

// ── Select styled ─────────────────────────────────────────────────────────────
const Select = styled.select`
  width:100%;padding:10px 14px;
  background:white;border:1.5px solid ${C.surfaceBorder};
  border-radius:10px;color:${C.text};font-size:14px;box-sizing:border-box;
  option{background:white;color:${C.text};}
  &:focus{outline:none;border-color:${C.primary};}
`;

// ── Page & Layout ─────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height:100vh;
  background:${C.bg};
  padding:28px 24px;
  font-family:'Inter','Segoe UI',sans-serif;
  color:${C.text};
`;
const Header = styled.div`
  max-width:1100px;margin:0 auto 28px;
  display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;
`;
const Title = styled.h1`
  font-size:24px;font-weight:900;color:${C.text};margin:0;
`;
const AddBtn = styled.button`
  padding:10px 22px;
  background:${C.primary};border:none;border-radius:10px;
  color:white;font-weight:700;font-size:14px;cursor:pointer;transition:all .18s;
  &:hover{background:${C.primaryDark};box-shadow:0 4px 14px rgba(37,99,235,.35);transform:translateY(-1px);}
`;

// ── Gift Grid & Card ──────────────────────────────────────────────────────────
const Grid = styled.div`
  max-width:1100px;margin:0 auto;
  display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:18px;
`;
const Card = styled.div`
  background:${C.surface};
  border:1.5px solid ${C.surfaceBorder};
  border-radius:16px;overflow:hidden;
  animation:${fadeIn} .25s ease;
  transition:transform .2s,box-shadow .2s;
  &:hover{transform:translateY(-3px);box-shadow:0 8px 24px rgba(0,0,0,.1);}
`;
const CardImg = styled.div`
  width:100%;height:160px;
  background:linear-gradient(135deg,#bfdbfe,#93c5fd);
  display:flex;align-items:center;justify-content:center;
  font-size:52px;overflow:hidden;position:relative;cursor:pointer;
`;
const CardImgEl = styled.img`
  width:100%;height:100%;object-fit:cover;position:absolute;inset:0;
  transition:transform .3s;&:hover{transform:scale(1.06);}
`;

// ── Lightbox ──────────────────────────────────────────────────────────────────
const LightboxOverlay = styled.div`
  position:fixed;inset:0;background:rgba(0,0,0,.92);
  display:flex;align-items:center;justify-content:center;
  z-index:2000;cursor:zoom-out;padding:24px;
`;
const LightboxImg = styled.img`
  max-width:90vw;max-height:88vh;object-fit:contain;
  border-radius:12px;box-shadow:0 0 60px rgba(0,0,0,.8);
`;

const CardBody = styled.div`padding:14px;`;
const CardName = styled.div`font-size:15px;font-weight:700;margin-bottom:6px;color:${C.text};`;
const QtyBadge = styled.span<{$empty:boolean}>`
  display:inline-block;padding:2px 10px;border-radius:20px;
  font-size:12px;font-weight:700;margin-bottom:8px;
  background:${p=>p.$empty?'#fee2e2':'#d1fae5'};
  color:${p=>p.$empty?'#dc2626':'#059669'};
  border:1px solid ${p=>p.$empty?'#fca5a5':'#6ee7b7'};
`;
const CardNote = styled.div`font-size:12px;color:${C.textMuted};margin-bottom:10px;`;
const CardActions = styled.div`display:flex;gap:7px;flex-wrap:wrap;`;

// Btn: text+border màu $color, hover = fill nhạt (không trùng màu chữ)
const Btn = styled.button<{$color?:string}>`
  flex:1;padding:7px 0;border-radius:8px;
  border:1.5px solid ${p=>p.$color||C.surfaceBorder};
  background:transparent;
  color:${p=>p.$color||C.textMuted};
  font-size:12px;font-weight:700;cursor:pointer;transition:all .15s;
  &:hover{
    background:${p=>p.$color?`${p.$color}18`:'rgba(0,0,0,.04)'};
    border-color:${p=>p.$color||C.textMuted};
  }
  &:disabled{opacity:.4;cursor:default;}
`;

// ── Modal overlay ─────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position:fixed;inset:0;background:rgba(15,23,42,.55);
  display:flex;align-items:center;justify-content:center;z-index:1000;padding:16px;
`;
const Modal = styled.div`
  background:white;border:1.5px solid ${C.surfaceBorder};
  border-radius:18px;padding:28px;width:100%;max-width:500px;
  max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.15);
`;
const ModalTitle = styled.h2`font-size:18px;font-weight:800;margin:0 0 20px;color:${C.text};`;
const Field = styled.div`margin-bottom:16px;`;
const Label = styled.label`display:block;font-size:12px;font-weight:700;color:${C.textMuted};margin-bottom:6px;letter-spacing:.4px;`;
const Input = styled.input`
  width:100%;padding:10px 14px;
  background:white;border:1.5px solid ${C.surfaceBorder};
  border-radius:10px;color:${C.text};font-size:14px;box-sizing:border-box;
  &:focus{outline:none;border-color:${C.primary};}
`;
const Textarea = styled.textarea`
  width:100%;padding:10px 14px;
  background:white;border:1.5px solid ${C.surfaceBorder};
  border-radius:10px;color:${C.text};font-size:14px;
  resize:vertical;min-height:80px;box-sizing:border-box;
  &:focus{outline:none;border-color:${C.primary};}
`;
const ModalFooter = styled.div`display:flex;gap:10px;margin-top:20px;justify-content:flex-end;`;
const SaveBtn = styled.button`
  padding:10px 24px;background:${C.primary};
  border:none;border-radius:10px;color:white;font-weight:700;cursor:pointer;
  transition:all .15s;
  &:hover{background:${C.primaryDark};}
  &:disabled{opacity:.5;cursor:default;}
`;
const CancelBtn = styled.button`
  padding:10px 18px;background:white;
  border:1.5px solid ${C.surfaceBorder};border-radius:10px;color:${C.textMuted};
  font-weight:700;cursor:pointer;transition:all .15s;
  &:hover{background:${C.bg};border-color:${C.textLight};}
`;

// ── History section ───────────────────────────────────────────────────────────
const HistoryWrap = styled.div`margin-top:14px;border-top:1px solid ${C.surfaceBorder};padding-top:12px;`;
const HistoryTitle = styled.div`font-size:11px;font-weight:700;color:${C.textLight};letter-spacing:.8px;text-transform:uppercase;margin-bottom:8px;`;
const HistoryItem = styled.div`font-size:12px;color:${C.textMuted};padding:6px 0;border-bottom:1px solid ${C.surfaceBorder};&:last-child{border-bottom:none;}`;

// ── History Modal ─────────────────────────────────────────────────────────────
const HistModalOverlay = styled.div`
  position:fixed;inset:0;background:rgba(15,23,42,.55);z-index:1500;
  display:flex;align-items:center;justify-content:center;padding:20px;
`;
const HistModalBox = styled.div`
  background:white;border:1.5px solid ${C.surfaceBorder};border-radius:20px;
  width:100%;max-width:700px;max-height:85vh;display:flex;flex-direction:column;
  box-shadow:0 20px 60px rgba(0,0,0,.15);
`;
const HistModalHead = styled.div`
  padding:20px 24px 16px;border-bottom:1.5px solid ${C.surfaceBorder};flex-shrink:0;background:white;border-radius:20px 20px 0 0;
`;
const HistModalBody = styled.div`
  flex:1;overflow-y:auto;padding:4px 0;background:white;border-radius:0 0 20px 20px;
  &::-webkit-scrollbar{width:5px;}
  &::-webkit-scrollbar-track{background:transparent;}
  &::-webkit-scrollbar-thumb{background:${C.surfaceBorder};border-radius:3px;}
`;
const HistSearch = styled.input`
  width:100%;padding:8px 12px;box-sizing:border-box;
  background:${C.bg};border:1.5px solid ${C.surfaceBorder};
  border-radius:10px;color:${C.text};font-size:13px;
  &:focus{outline:none;border-color:${C.primary};}
  &::placeholder{color:${C.textLight};}
`;
const HistFilterSelect = styled.select`
  padding:8px 12px;background:rgba(255,255,255,.07);
  border:1px solid rgba(255,255,255,.12);border-radius:10px;color:#f1f5f9;font-size:13px;
  option{background:#1e293b;}
  &:focus{outline:none;border-color:#7c3aed;}
`;
const HistRow = styled.div`
  display:flex;align-items:center;gap:10px;padding:10px 24px;
  border-bottom:1px solid rgba(255,255,255,.05);
  &:last-child{border-bottom:none;}
  &:hover{background:rgba(255,255,255,.03);}
`;

// ── Empty state ──────────────────────────────────────────────────────────────
const Empty = styled.div`
  max-width:1100px;margin:60px auto;text-align:center;color:#475569;
`;

// ── Types ────────────────────────────────────────────────────────────────────
interface Gift { id:number;name:string;quantity:number;image?:string|null;note?:string|null;totalGifted?:number; }
interface Transaction { id:number;studentId?:number|null;studentName:string;quantity:number;note?:string|null;giftedAt:string; }

// ── helpers ──────────────────────────────────────────────────────────────────
function imgUrl(img?:string|null){
  if(!img) return null;
  if(img.startsWith('http')||img.startsWith('data:')) return img;
  return `${getApiUrl()}${img}`;
}

// ── Component ────────────────────────────────────────────────────────────────
const GiftManage: React.FC = () => {
  const dispatch = useAppDispatch();
  const classes = useAppSelector((s:any)=>s.classes.classesList||[]);
  const allStudents = useAppSelector((s:any)=>s.students.studentsList||[]);
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('userInfo')||'{}').role===0; } catch{return false;} })();

  const [tab, setTab] = useState<'kho'|'class'|'random'>('kho');
  const [lightboxSrc, setLightboxSrc] = useState<string|null>(null);

  // ── Tab Kho ──────────────────────────────────────────────────────────────
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Gift|null>(null);
  const [form, setForm] = useState({name:'',quantity:'0',note:''});
  const [imageFile, setImageFile] = useState<File|null>(null);
  const [imagePreview, setImagePreview] = useState<string|null>(null);
  const [removeImg, setRemoveImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyMap, setHistoryMap] = useState<Record<number,Transaction[]>>({});
  const [historyModal, setHistoryModal] = useState<{gift:Gift}|null>(null);
  const [histSearch, setHistSearch] = useState('');
  const [histClassFilter, setHistClassFilter] = useState('');
  const [loadingHistModal, setLoadingHistModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Gift|null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try { setGifts(await giftService.getAll()); } catch{}
    setLoading(false);
  };

  // Refresh danh sách mà không gây nhiễu UI (không set loading)
  const silentRefresh = async () => {
    try { setGifts(await giftService.getAll()); } catch{}
  };

  useEffect(()=>{
    load();
    dispatch(classActions.getAll());
    dispatch(studentActions.getAll());
  },[dispatch]);

  const openAdd = () => {
    setEditing(null);
    setForm({name:'',quantity:'0',note:''});
    setImageFile(null); setImagePreview(null); setRemoveImg(false);
    setShowModal(true);
  };

  const openEdit = (g:Gift) => {
    setEditing(g);
    setForm({name:g.name,quantity:String(g.quantity),note:g.note||''});
    setImageFile(null);
    setImagePreview(imgUrl(g.image));
    setRemoveImg(false);
    setShowModal(true);
  };

  const handleImage = (e:React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if(!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
    setRemoveImg(false);
  };

  const handleSave = async () => {
    if(!form.name.trim()) return alert('Nhập tên phần quà');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('quantity', form.quantity||'0');
      fd.append('note', form.note||'');
      if(imageFile) fd.append('image', imageFile);
      if(removeImg) fd.append('removeImage','true');
      if(editing) await giftService.update(editing.id, fd);
      else await giftService.create(fd);
      setShowModal(false);
      silentRefresh(); // không gây flash
      dispatch(notificationActions.addNotification(editing?'Cập nhật quà thành công!':'Thêm phần quà thành công!','SUCCESS'));
    } catch(e:any){ dispatch(notificationActions.addNotification(String(e)||'Lỗi lưu quà','DANGER')); }
    setSaving(false);
  };

  const handleDelete = async (g:Gift) => {
    try {
      await giftService.remove(g.id);
      setConfirmDelete(null);
      // Optimistic: xóa khỏi list ngay
      setGifts(prev => prev.filter(x => x.id !== g.id));
      setHistoryMap(m => { const n={...m}; delete n[g.id]; return n; });
      if(historyModal?.gift.id===g.id) setHistoryModal(null);
      dispatch(notificationActions.addNotification(`Đã xóa phần quà ${g.name}`, 'SUCCESS'));
    }
    catch(e:any){ dispatch(notificationActions.addNotification(String(e)||'Lỗi xóa','DANGER')); }
  };

  const openHistoryModal = async (g:Gift) => {
    setHistoryModal({gift:g});
    setHistSearch(''); setHistClassFilter('');
    if(historyMap[g.id]) return; // cache
    setLoadingHistModal(true);
    try { const txs=await giftService.getTransactions(g.id); setHistoryMap(m=>({...m,[g.id]:txs})); } catch{}
    setLoadingHistModal(false);
  };

  // ── Tab Tặng quà ─────────────────────────────────────────────────────────
  const [tGift, setTGift] = useState<Gift|null>(null);
  const [tClassId, setTClassId] = useState('');
  const [tStudentId, setTStudentId] = useState('');
  const [tQty, setTQty] = useState('1');
  const [tNote, setTNote] = useState('');
  const [tConfirm, setTConfirm] = useState(false);
  const [tSaving, setTSaving] = useState(false);
  const tStudents = tClassId ? allStudents.filter((s:any)=>String(s.classId)===tClassId&&!s.archivedAt) : [];
  const tStudent = allStudents.find((s:any)=>String(s.id)===tStudentId);

  const handleGive = useCallback(async()=>{
    if(!tGift||!tStudentId) return;
    setTSaving(true);
    try{
      const res = await giftService.giveGift(tGift.id,{studentId:Number(tStudentId),studentName:tStudent?.name||'',quantity:Number(tQty)||1,note:tNote||undefined});
      const giftName=tGift.name; const stuName=tStudent?.name; const qty=Number(tQty)||1;
      const giftId=tGift.id;
      setTConfirm(false);
      setTGift(null);setTClassId('');setTStudentId('');setTQty('1');setTNote('');
      // Optimistic: giảm quantity trong list ngay
      setGifts(prev=>prev.map(g=>g.id===giftId?{...g,quantity:g.quantity-qty,totalGifted:(g.totalGifted||0)+qty}:g));
      // Prepend vào history nếu đang mở
      if(res?.transaction){
        setHistoryMap(m=>({...m,[giftId]:[res.transaction,...(m[giftId]||[])]}));
      }
      dispatch(notificationActions.addNotification(`✅ Đã tặng ${qty}× ${giftName} cho ${stuName}!`,'SUCCESS'));
    }catch(e:any){dispatch(notificationActions.addNotification(String(e)||'Lỗi tặng quà','DANGER'));}
    setTSaving(false);
  },[tGift,tStudentId,tQty,tNote,tStudent]);

  // ── Tab Random ────────────────────────────────────────────────────────────
  const [rPicking, setRPicking] = useState(false);
  const [rHighlight, setRHighlight] = useState<number|null>(null);
  const [rWinner, setRWinner] = useState<Gift|null>(null);
  const [rShowAssign, setRShowAssign] = useState(false);
  const [rClassId, setRClassId] = useState('');
  const [rStudentId, setRStudentId] = useState('');
  const [rQty, setRQty] = useState('1');
  const [rNote, setRNote] = useState('');
  const [rSaving, setRSaving] = useState(false);
  const rStudents = rClassId ? allStudents.filter((s:any)=>String(s.classId)===rClassId&&!s.archivedAt) : [];
  const rStudent = allStudents.find((s:any)=>String(s.id)===rStudentId);
  const availableGifts = gifts.filter(g=>g.quantity>0);

  const pickRandom = useCallback(()=>{
    if(rPicking||availableGifts.length===0) return;
    setRPicking(true); setRWinner(null); setRShowAssign(false);
    let i=0; const total=20+Math.floor(Math.random()*15);
    const winnerIdx=Math.floor(Math.random()*availableGifts.length);
    const run=()=>{
      setRHighlight(availableGifts[i%availableGifts.length].id);
      i++;
      const delay=i<total*0.7?80:i<total?150:300;
      if(i<total) setTimeout(run,delay);
      else{setRHighlight(null);setRPicking(false);setRWinner(availableGifts[winnerIdx]);}
    };
    setTimeout(run,80);
  },[rPicking,availableGifts]);

  const handleRAssign = useCallback(async()=>{
    if(!rWinner||!rStudentId) return;
    setRSaving(true);
    try{
      const res = await giftService.giveGift(rWinner.id,{studentId:Number(rStudentId),studentName:rStudent?.name||'',quantity:Number(rQty)||1,note:rNote||undefined});
      const wName=rWinner.name; const sName=rStudent?.name; const qty=Number(rQty)||1; const giftId=rWinner.id;
      setRShowAssign(false);setRWinner(null);setRClassId('');setRStudentId('');setRQty('1');setRNote('');
      // Optimistic update
      setGifts(prev=>prev.map(g=>g.id===giftId?{...g,quantity:g.quantity-qty,totalGifted:(g.totalGifted||0)+qty}:g));
      if(res?.transaction){
        setHistoryMap(m=>({...m,[giftId]:[res.transaction,...(m[giftId]||[])]}));
      }
      dispatch(notificationActions.addNotification(`✅ Đã gán ${wName} cho ${sName}!`,'SUCCESS'));
    }catch(e:any){dispatch(notificationActions.addNotification(String(e)||'Lỗi gán quà','DANGER'));}
    setRSaving(false);
  },[rWinner,rStudentId,rQty,rNote,rStudent]);

  // ── Edit Transaction ──────────────────────────────────────────────────────────
  const [editTx, setEditTx] = useState<{tx:Transaction;giftId:number}|null>(null);
  const [etGiftId, setEtGiftId] = useState('');
  const [etQty, setEtQty] = useState('1');
  const [etNote, setEtNote] = useState('');
  const [etSaving, setEtSaving] = useState(false);

  const openEditTx = (tx:Transaction, giftId:number) => {
    setEditTx({tx,giftId});
    setEtGiftId(String(giftId));
    setEtQty(String(tx.quantity));
    setEtNote(tx.note||'');
  };

  const handleEditTx = async () => {
    if(!editTx) return;
    setEtSaving(true);
    try{
      const res = await giftService.updateTransaction(editTx.tx.id,{
        newGiftId: Number(etGiftId)!==editTx.giftId?Number(etGiftId):undefined,
        quantity:Number(etQty)||1,
        note:etNote||undefined,
      });
      const oldGiftId=editTx.giftId; const newGiftId=Number(etGiftId); const oldQty=editTx.tx.quantity; const newQty=Number(etQty)||1;
      setEditTx(null);
      // Optimistic: cập nhật quantity trong gifts state
      setGifts(prev=>prev.map(g=>{
        if(g.id===oldGiftId && oldGiftId!==newGiftId) return {...g,quantity:g.quantity+oldQty};
        if(g.id===newGiftId && oldGiftId!==newGiftId) return {...g,quantity:g.quantity-newQty};
        if(g.id===oldGiftId && oldGiftId===newGiftId) return {...g,quantity:g.quantity+oldQty-newQty};
        return g;
      }));
      // Refresh history cho cả 2 gift
      const txs = await giftService.getTransactions(oldGiftId);
      setHistoryMap(m=>({...m,[oldGiftId]:txs}));
      if(newGiftId!==oldGiftId){
        const txs2=await giftService.getTransactions(newGiftId);
        setHistoryMap(m=>({...m,[newGiftId]:txs2}));
      }
      dispatch(notificationActions.addNotification('Cập nhật giao dịch thành công!','SUCCESS'));
    }catch(e:any){dispatch(notificationActions.addNotification(String(e)||'Lỗi cập nhật','DANGER'));}
    setEtSaving(false);
  };

  const handleDeleteTx = async (tx:Transaction, giftId:number) => {
    if(!window.confirm(`Huỷ tặng quà cho ${tx.studentName}? Kho sẽ được hoàn lại ${tx.quantity} phần.`)) return;
    try{
      await giftService.deleteTransaction(tx.id);
      // Optimistic: hoàn kho + giảm totalGifted ngay
      setGifts(prev=>prev.map(g=>g.id===giftId?{...g,quantity:g.quantity+tx.quantity,totalGifted:Math.max(0,(g.totalGifted||0)-tx.quantity)}:g));
      setHistoryMap(m=>({...m,[giftId]:(m[giftId]||[]).filter(t=>t.id!==tx.id)}));
      // Cập nhật gift trong historyModal nếu đang mở
      setHistoryModal(prev=>prev&&prev.gift.id===giftId?{gift:{...prev.gift,quantity:prev.gift.quantity+tx.quantity,totalGifted:Math.max(0,(prev.gift.totalGifted||0)-tx.quantity)}}:prev);
      dispatch(notificationActions.addNotification(`Đã huỷ tặng quà và hoàn kho ${tx.quantity} phần!`,'SUCCESS'));
    }catch(e:any){dispatch(notificationActions.addNotification(String(e)||'Lỗi huỷ','DANGER'));}
  };

  return (
    <Page>
      <Header>
        <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          <Title>🎁 Quản lý Quà tặng</Title>
          {!loading && gifts.length > 0 && isAdmin && (
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <span style={{
                padding:'4px 12px',borderRadius:20,fontSize:13,fontWeight:700,
                background:'#d1fae5',color:'#059669',
                border:'1px solid #6ee7b7',
              }}>
                📦 Còn lại: {gifts.reduce((s,g)=>s+g.quantity,0)}
              </span>
              <span style={{
                padding:'4px 12px',borderRadius:20,fontSize:13,fontWeight:700,
                background:'#fef9c3',color:'#92400e',
                border:'1px solid #fde68a',
              }}>
                🎀 Đã tặng: {gifts.reduce((s,g)=>s+(g.totalGifted||0),0)}
              </span>
            </div>
          )}
        </div>
        {tab==='kho' && <AddBtn onClick={openAdd}>+ Thêm phần quà</AddBtn>}
      </Header>

      <TabBar>
        <Tab $active={tab==='kho'} onClick={()=>setTab('kho')}>🎁 Kho quà</Tab>
        <Tab $active={tab==='class'} onClick={()=>setTab('class')}>🏫 Tặng cả lớp</Tab>
        <Tab $active={tab==='random'} onClick={()=>setTab('random')}>🎲 Chọn ngẫu nhiên</Tab>
      </TabBar>

      {/* ── TAB KHO QUÀ ──────────────────────────────────────────────────── */}
      {tab==='kho' && (loading ? (
        <Empty>⏳ Đang tải...</Empty>
      ) : gifts.length === 0 ? (
        <Empty>
          <div style={{fontSize:56,marginBottom:12}}>🎁</div>
          <div style={{fontSize:16,marginBottom:16}}>Chưa có phần quà nào</div>
          <AddBtn onClick={openAdd}>+ Thêm phần quà đầu tiên</AddBtn>
        </Empty>
      ) : (
        <Grid>
          {gifts.map(g=>(
            <Card key={g.id}>
              <CardImg
                title={imgUrl(g.image) ? 'Bấm để xem ảnh lớn' : ''}
                onClick={()=>{ if(imgUrl(g.image)) setLightboxSrc(imgUrl(g.image)); }}
                style={!imgUrl(g.image)?{background:'linear-gradient(135deg,#4f46e5,#7c3aed)'}:{background:'#0f172a'}}
              >
                {imgUrl(g.image)
                  ? <CardImgEl src={imgUrl(g.image)!} alt={g.name} />
                  : '🎁'
                }
              </CardImg>
              <CardBody>
                <CardName>{g.name}</CardName>
                <QtyBadge $empty={g.quantity===0}>
                  {g.quantity===0 ? '⚠️ Hết quà' : `📦 Còn: ${g.quantity}`}
                </QtyBadge>
                {(g.totalGifted||0) > 0 && (
                  <span style={{display:'inline-block',marginLeft:6,padding:'2px 10px',borderRadius:20,fontSize:12,fontWeight:700,background:'rgba(251,191,36,.12)',color:'#fbbf24',border:'1px solid rgba(251,191,36,.2)'}}>
                    🎀 Đã tặng: {g.totalGifted}
                  </span>
                )}
                {g.note && <CardNote>{g.note}</CardNote>}
                <CardActions>
                  <Btn $color="#60a5fa" onClick={()=>openEdit(g)}>✏️ Sửa</Btn>
                  <Btn $color="#f87171" onClick={()=>setConfirmDelete(g)}>🗑 Xóa</Btn>
                  <Btn $color="#34d399" style={{flex:'100%'}}
                    disabled={g.quantity===0}
                    onClick={()=>{
                      setTGift(g);setTClassId('');setTStudentId('');setTQty('1');setTNote('');
                      setTConfirm(true);
                    }}
                  >
                    {g.quantity===0?'⚠️ Hết quà':'🎀 Tặng cho học sinh'}
                  </Btn>
                  <Btn $color="#a78bfa" style={{flex:'100%'}} onClick={()=>openHistoryModal(g)}>
                    📋 Lịch sử tặng {(g.totalGifted||0)>0&&`(${g.totalGifted})`}
                  </Btn>
                </CardActions>
              </CardBody>
            </Card>
          ))}
        </Grid>
      ))}

      {/* ── TAB TẶNG CẢ LỚP ──────────────────────────────────────────────── */}
      {tab==='class' && (
        <GiftToClass gifts={gifts} onGiftsUpdated={silentRefresh} />
      )}

      {/* ── TAB CHỌN NGẪU NHIÊN ───────────────────────────────────────────── */}
      {tab==='random' && (
        <div style={{maxWidth:1100,margin:'0 auto'}}>
          {availableGifts.length===0 ? (
            <Empty><div style={{fontSize:48,marginBottom:12}}>📦</div>Tất cả phần quà đã hết kho</Empty>
          ):(
            <>
              <div style={{textAlign:'center',marginBottom:24}}>
                <AddBtn onClick={pickRandom} disabled={rPicking} style={{fontSize:18,padding:'14px 36px'}}>
                  {rPicking?'🎲 Đang chọn...':'🎲 Chọn ngẫu nhiên!'}
                </AddBtn>
              </div>
              <Grid>
                {availableGifts.map(g=>{
                  const isHL=rHighlight===g.id;
                  const isWin=rWinner?.id===g.id;
                  return(
                    <Card key={g.id} style={{
                      border:`2px solid ${isWin?'#fbbf24':isHL?'#a78bfa':'rgba(255,255,255,.1)'}`,
                      transform:isHL||isWin?'scale(1.04)':'none',
                      boxShadow:isWin?'0 0 32px rgba(251,191,36,.5)':isHL?'0 0 20px rgba(167,139,250,.5)':'none',
                      transition:'all .1s',
                    }}>
                      <CardImg
                        style={!imgUrl(g.image)?{background:'linear-gradient(135deg,#4f46e5,#7c3aed)'}:{background:'#0f172a'}}
                        onClick={()=>{ if(imgUrl(g.image)) setLightboxSrc(imgUrl(g.image)); }}
                        title={imgUrl(g.image)?'Bấm để xem ảnh lớn':''}
                      >
                        {imgUrl(g.image)
                          ? <CardImgEl src={imgUrl(g.image)!} alt={g.name} />
                          : '🎁'
                        }
                      </CardImg>
                      <CardBody>
                        <CardName>{isWin&&'🏆 '}{g.name}</CardName>
                        <QtyBadge $empty={false}>📦 Còn: {g.quantity}</QtyBadge>
                      </CardBody>
                    </Card>
                  );
                })}
              </Grid>
              {rWinner && (
                <div style={{maxWidth:500,margin:'32px auto 0',background:'rgba(251,191,36,.1)',border:'2px solid rgba(251,191,36,.4)',borderRadius:20,padding:24,textAlign:'center'}}>
                  <div style={{fontSize:40,marginBottom:8}}>🏆</div>
                  <div style={{fontSize:22,fontWeight:900,color:'#fbbf24',marginBottom:16}}>{rWinner.name}</div>
                  {!rShowAssign?(
                    <AddBtn onClick={()=>setRShowAssign(true)} style={{width:'100%'}}>🎀 Gán cho học sinh ngay</AddBtn>
                  ):(
                    <>
                      <Field>
                        <Label>Chọn lớp</Label>
                        <Select value={rClassId} onChange={e=>{setRClassId(e.target.value);setRStudentId('');}}>
                          <option value=''>-- Chọn lớp --</option>
                          {[...classes].sort((a:any,b:any)=>a.name.localeCompare(b.name)).map((c:any)=><option key={c.id} value={c.id}>{c.name}</option>)}
                        </Select>
                      </Field>
                      <Field>
                        <Label>Chọn học sinh</Label>
                        <Select value={rStudentId} onChange={e=>setRStudentId(e.target.value)} disabled={!rClassId}>
                          <option value=''>-- Chọn học sinh --</option>
                          {rStudents.map((s:any)=><option key={s.id} value={s.id}>{s.name}</option>)}
                        </Select>
                      </Field>
                      <Field>
                        <Label>Số lượng</Label>
                        <Input type='number' min='1' max={rWinner.quantity} value={rQty} onChange={e=>setRQty(e.target.value)} />
                      </Field>
                      <Field>
                        <Label>Ghi chú</Label>
                        <Textarea value={rNote} onChange={e=>setRNote(e.target.value)} placeholder='Ghi chú...' />
                      </Field>
                      <AddBtn disabled={!rStudentId||rSaving} style={{width:'100%',opacity:!rStudentId?.5:1}} onClick={handleRAssign}>
                        {rSaving?'Đang gán...':'✅ Xác nhận gán quà'}
                      </AddBtn>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {/* ── Modal lịch sử tặng quà ─────────────────────────────────────── */}
      {historyModal && (() => {
        const g = historyModal.gift;
        const allTxs = historyMap[g.id] || [];
        // Lấy danh sách lớp có trong lịch sử để filter
        const txClassIds = new Set(allTxs.map(tx=>{
          if(!tx.studentId) return null;
          const s = allStudents.find((s:any)=>s.id===tx.studentId);
          return s?.classId ?? null;
        }).filter(Boolean));
        const histClasses = classes.filter((c:any)=>txClassIds.has(c.id));
        // Filter theo search + class
        const filtered = allTxs.filter(tx=>{
          const nameMatch = !histSearch || tx.studentName.toLowerCase().includes(histSearch.toLowerCase());
          if(!nameMatch) return false;
          if(!histClassFilter) return true;
          const s = tx.studentId ? allStudents.find((ss:any)=>ss.id===tx.studentId) : null;
          return s ? String(s.classId)===histClassFilter : false;
        });
        return (
          <HistModalOverlay onClick={e=>{if(e.target===e.currentTarget)setHistoryModal(null);}}>
            <HistModalBox>
              <HistModalHead>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                  <div>
                    <div style={{fontSize:18,fontWeight:800,color:'#1e293b'}}>📋 Lịch sử tặng — <span style={{color:'#2563eb'}}>{g.name}</span></div>
                    <div style={{fontSize:12,color:'#64748b',marginTop:2}}>Tổng: {allTxs.length} lần tặng</div>
                  </div>
                  <button onClick={()=>setHistoryModal(null)} style={{background:'#f1f5f9',border:'1px solid #e2e8f0',color:'#64748b',fontSize:20,width:36,height:36,borderRadius:'50%',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
                </div>
                <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:160}}>
                    <HistSearch
                      placeholder="🔍 Tìm tên học sinh..."
                      value={histSearch}
                      onChange={e=>setHistSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <HistFilterSelect value={histClassFilter} onChange={e=>setHistClassFilter(e.target.value)}>
                    <option value=''>Tất cả lớp</option>
                    {[...histClasses].sort((a:any,b:any)=>a.name.localeCompare(b.name)).map((c:any)=>(
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </HistFilterSelect>
                </div>
              </HistModalHead>

              <HistModalBody>
                {loadingHistModal && <div style={{padding:'20px 24px',color:'#64748b',textAlign:'center'}}>⏳ Đang tải...</div>}
                {!loadingHistModal && filtered.length===0 && (
                  <div style={{padding:'32px 24px',color:'#64748b',textAlign:'center'}}>
                    {allTxs.length===0 ? '📭 Chưa có lịch sử tặng quà' : '🔍 Không tìm thấy kết quả phù hợp'}
                  </div>
                )}
                {filtered.map(tx=>{
                  const stuInfo = tx.studentId ? allStudents.find((s:any)=>s.id===tx.studentId) : null;
                  const classInfo = stuInfo ? classes.find((c:any)=>c.id===stuInfo.classId) : null;
                  return (
                    <HistRow key={tx.id}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:3}}>
                          {classInfo && (
                            <span style={{padding:'1px 8px',borderRadius:10,fontSize:11,fontWeight:700,background:'#eff6ff',color:'#1d4ed8',border:'1px solid #bfdbfe',flexShrink:0}}>
                              {classInfo.name}
                            </span>
                          )}
                          <strong style={{fontSize:13,color:'#1e293b'}}>{tx.studentName}</strong>
                          <span style={{fontSize:12,color:'#059669',fontWeight:700}}>×{tx.quantity}</span>
                        </div>
                        <div style={{fontSize:11,color:'#64748b',display:'flex',gap:8,flexWrap:'wrap'}}>
                          <span>{new Date(tx.giftedAt).toLocaleString('vi-VN')}</span>
                          {tx.note && <span>· {tx.note}</span>}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:4,flexShrink:0}}>
                        <button onClick={()=>{openEditTx(tx,g.id);setHistoryModal(null);}} style={{padding:'3px 9px',fontSize:11,borderRadius:6,border:'1px solid #2563eb',background:'transparent',color:'#2563eb',cursor:'pointer'}}>✏️ Sửa</button>
                        <button onClick={()=>handleDeleteTx(tx,g.id)} style={{padding:'3px 9px',fontSize:11,borderRadius:6,border:'1px solid #ef4444',background:'transparent',color:'#ef4444',cursor:'pointer'}}>🚫 Huỷ</button>
                      </div>
                    </HistRow>
                  );
                })}
              </HistModalBody>
            </HistModalBox>
          </HistModalOverlay>
        );
      })()}

      {/* Modal sửa giao dịch tặng quà */}
      {editTx && (
        <Overlay onClick={e=>{if(e.target===e.currentTarget)setEditTx(null);}}>
          <Modal>
            <ModalTitle>✏️ Sửa giao dịch tặng quà</ModalTitle>
            <p style={{color:'#64748b',fontSize:13,marginBottom:16}}>Học sinh: <strong style={{color:'#1e293b'}}>{editTx.tx.studentName}</strong></p>
            <Field>
              <Label>Đổi sang phần quà</Label>
              <Select value={etGiftId} onChange={e=>setEtGiftId(e.target.value)}>
                {gifts.map(g=><option key={g.id} value={g.id}>{g.name} (kho: {g.id===editTx.giftId?g.quantity+editTx.tx.quantity:g.quantity})</option>)}
              </Select>
            </Field>
            <Field>
              <Label>Số lượng</Label>
              <Input type='number' min='1' value={etQty} onChange={e=>setEtQty(e.target.value)} />
            </Field>
            <Field>
              <Label>Ghi chú</Label>
              <Textarea value={etNote} onChange={e=>setEtNote(e.target.value)} placeholder='Ghi chú...' />
            </Field>
            <ModalFooter>
              <CancelBtn onClick={()=>setEditTx(null)}>Huỷ</CancelBtn>
              <SaveBtn disabled={etSaving} onClick={handleEditTx}>{etSaving?'Đang lưu...':'💾 Lưu thay đổi'}</SaveBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {lightboxSrc && (
        <LightboxOverlay onClick={()=>setLightboxSrc(null)}>
          <LightboxImg src={lightboxSrc} alt="Gift image" onClick={e=>e.stopPropagation()} />
          <button onClick={()=>setLightboxSrc(null)} style={{
            position:'fixed',top:20,right:24,background:'rgba(255,255,255,.15)',
            border:'none',color:'white',fontSize:28,width:44,height:44,borderRadius:'50%',
            cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
            backdropFilter:'blur(4px)'
          }}>×</button>
        </LightboxOverlay>
      )}

      {/* Modal tặng quà — form đầy đủ */}
      {tConfirm && tGift && (
        <Overlay onClick={e=>{if(e.target===e.currentTarget)setTConfirm(false);}}>
          <Modal>
            <ModalTitle>🎀 Tặng quà: <span style={{color:'#fbbf24'}}>{tGift.name}</span></ModalTitle>
            <p style={{color:'#64748b',fontSize:13,marginBottom:16}}>📦 Kho còn: <strong style={{color:'#34d399'}}>{tGift.quantity}</strong></p>
            <Field>
              <Label>Chọn lớp *</Label>
              <Select value={tClassId} onChange={e=>{setTClassId(e.target.value);setTStudentId('');}}>
                <option value=''>-- Chọn lớp --</option>
                {[...classes].sort((a:any,b:any)=>a.name.localeCompare(b.name)).map((c:any)=><option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field>
              <Label>Chọn học sinh *</Label>
              <Select value={tStudentId} onChange={e=>setTStudentId(e.target.value)} disabled={!tClassId}>
                <option value=''>-- Chọn học sinh --</option>
                {tStudents.map((s:any)=><option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
            <Field>
              <Label>Số lượng tặng</Label>
              <Input type='number' min='1' max={tGift.quantity} value={tQty} onChange={e=>setTQty(e.target.value)} />
            </Field>
            <Field>
              <Label>Ghi chú (tuỳ chọn)</Label>
              <Textarea value={tNote} onChange={e=>setTNote(e.target.value)} placeholder='VD: Thưởng vì phát biểu tốt...' />
            </Field>
            {tStudent && (
              <div style={{background:'rgba(52,211,153,.08)',border:'1px solid rgba(52,211,153,.25)',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#6ee7b7'}}>
                ✅ Tặng <strong>{tQty}x {tGift.name}</strong> cho <strong>{tStudent.name}</strong> → kho còn {tGift.quantity - Number(tQty)}
              </div>
            )}
            <ModalFooter>
              <CancelBtn onClick={()=>setTConfirm(false)}>Huỷ</CancelBtn>
              <SaveBtn disabled={!tStudentId||tSaving} style={{opacity:!tStudentId?0.5:1}} onClick={handleGive}>
                {tSaving?'Đang tặng...':'🎀 Xác nhận tặng'}
              </SaveBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {showModal && (
        <Overlay onClick={e=>{if(e.target===e.currentTarget)setShowModal(false);}}>
          <Modal>
            <ModalTitle>{editing?'✏️ Sửa phần quà':'🎁 Thêm phần quà mới'}</ModalTitle>
            <Field>
              <Label>Tên phần quà *</Label>
              <Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="VD: Bút bi, Gấu bông..." />
            </Field>
            <Field>
              <Label>Số lượng trong kho</Label>
              <Input type="number" min="0" value={form.quantity} onChange={e=>setForm(f=>({...f,quantity:e.target.value}))} />
            </Field>
            <Field>
              <Label>Ghi chú</Label>
              <Textarea value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} placeholder="Ghi chú thêm về phần quà..." />
            </Field>
            <Field>
              <Label>Hình ảnh</Label>
              {imagePreview && (
                <div style={{marginBottom:10,position:'relative',display:'inline-block'}}>
                  <img src={imagePreview} alt="preview" style={{width:120,height:90,objectFit:'cover',borderRadius:10,border:'1px solid rgba(255,255,255,.1)'}} />
                  <button onClick={()=>{setImagePreview(null);setImageFile(null);setRemoveImg(true);if(fileRef.current)fileRef.current.value='';}}
                    style={{position:'absolute',top:-8,right:-8,width:22,height:22,borderRadius:'50%',border:'none',background:'#ef4444',color:'white',cursor:'pointer',fontWeight:900,fontSize:14,lineHeight:'22px',textAlign:'center'}}>×</button>
                </div>
              )}
              <div>
                <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImage} />
                <Btn $color="#60a5fa" style={{display:'inline-block',padding:'7px 16px',flex:'none'}} onClick={()=>fileRef.current?.click()}>
                  📷 {imagePreview?'Đổi ảnh':'Chọn ảnh'}
                </Btn>
              </div>
            </Field>
            <ModalFooter>
              <CancelBtn onClick={()=>setShowModal(false)}>Huỷ</CancelBtn>
              <SaveBtn disabled={saving} onClick={handleSave}>{saving?'Đang lưu...':'💾 Lưu'}</SaveBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {/* Modal xác nhận xóa */}
      {confirmDelete && (
        <Overlay onClick={e=>{if(e.target===e.currentTarget)setConfirmDelete(null);}}>
          <Modal style={{maxWidth:380}}>
            <ModalTitle>🗑 Xóa phần quà</ModalTitle>
            <p style={{color:'#94a3b8',marginBottom:8}}>Bạn chắc chắn muốn xóa phần quà <strong style={{color:'#f1f5f9'}}>{confirmDelete.name}</strong>?</p>
            <p style={{color:'#ef4444',fontSize:13}}>⚠️ Lịch sử tặng quà sẽ bị xóa theo.</p>
            <ModalFooter>
              <CancelBtn onClick={()=>setConfirmDelete(null)}>Huỷ</CancelBtn>
              <SaveBtn style={{background:'linear-gradient(135deg,#dc2626,#b91c1c)'}} onClick={()=>handleDelete(confirmDelete)}>Xóa hẳn</SaveBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
};

export default GiftManage;
